import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { ALL_PLATFORM_ICONS, PLATFORM_COLORS } from '../../../../config/icons';
import FacebookPostPreview from '../SocialMediaCards/FacebookPostPreview';
import InstagramPostPreview from '../SocialMediaCards/InstagramPostPreview';
import TwitterPostPreview from '../SocialMediaCards/TwitterPostPreview';
import GooglePostPreview from '../SocialMediaCards/GooglePostPreview';
import LinkedinPostPreview from '../SocialMediaCards/LinkedinPostPreview';
import GenericPostPreview from '../SocialMediaCards/GenericPostPreview';
import WhatsAppPostPreview from '../SocialMediaCards/WhatsAppPostPreview';
import YoutubePostPreview from '../SocialMediaCards/YoutubePostPreview';
import InstagramFacebookStory from '../SocialMediaCards/InstagramFacebookStory';
import {
  getProfileDataForPlatform,
  saveCroppedImageCache,
  switchPreviewMode,
  updateTemplateBlob
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import {
  ALL,
  FACEBOOK,
  GENERIC,
  GOOGLE,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE,
  LINKEDIN_PERSONAL_ACCOUNT,
  PINTEREST,
  PLATFORM_LIST,
  PLATFORM_LIST_TOLGEE_NAMES,
  PLATFORMS_COLORS,
  TIKTOK_BUSINESS_PAGE,
  TIKTOK_PERSONAL_ACCOUNT,
  TWITTER,
  WHATSAPP,
  YOUTUBE
} from '../../../../config/constants';
import {
  POST_TYPE_MESSAGE,
  POST_TYPE_POST_KEY,
  POST_TYPE_REEL_KEY,
  POST_TYPE_TRIAL_REEL_KEY,
  POST_TYPE_STORY_KEY,
  POST_TYPE_TEMPLATE,
  POST_TYPE_VIDEO_KEY
} from '../../config/platformFeatures';
import { PostPreviewTitle } from '../SocialMediaCards/postPreviewTtitle';
import { cropImage } from '../../../../utils/media/cropImage';
import { T, useTranslate } from '@tolgee/react';
import { Dropdown } from 'react-bootstrap';
import {
  POST_PUBLISHED_ON_PLATFORM,
  PREVIEW_TYPE_COMPACT,
  PREVIEW_TYPE_STANDARD
} from '../../config/constants';
import { useClickAway } from 'react-use';
import TiktokPostPreview from '../SocialMediaCards/tiktokPostPreview';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import './custom-simple-bar.css';
import { getPlatformsWithPreview, showPreview } from '../../utils/helpers';
import { getPlatformTranslationKey } from '../../../../utils/utils';
import { createCanvasWithOverlay } from '../../utils/brandOverlay';
import { Md5 } from 'ts-md5';
import { DEFAULT_EDITOR_FONT_URL } from '../contants';
import {
  getOverlayTemplateEligibleMedia,
  shouldShowOverlayTemplatePreview,
  OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR
} from '../../config/overlayTemplateCreator';

interface Props {
  platformEditorSwitch: Function;
  handleMediaView: Function;
  templatePreviewUrl?: string | null;
  templateMode?: boolean;
}
interface MediaFilesMap {
  [platform: string]: any[]; // Define mediaFilesMap type
}

const PREVIEW_CARD_WIDTH = 500;

const MediaUploadPreview: FC<Props> = ({
  platformEditorSwitch,
  handleMediaView,
  templatePreviewUrl,
  templateMode = false
}) => {
  const [postHasPreview, setPostHasPreview] = React.useState(false);
  const { t } = useTranslate();
  const [mediaFilesMap, setMediaFilesMap] = useState<MediaFilesMap>({});
  const dispatch = useDispatch();
  const previewModeRef = useRef(null);
  const [previewModeOpen, setPreviewModeOpen] = React.useState(false);
  const {
    postData,
    lomavisCreatorConfig,
    postTypeSelected,
    cropsCache,
    platformEnabled,
    locationUuid,
    createdOverlayArray,
    brandData,
    overlayData,
    ui: { previewMode }
  } = useSelector((state: lomavisCreatorRootState) => state.lomavisCreator);
  const location_slug = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.lomavisCreatorConfig?.location?.slug
  );

  const postIsOverlay = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.overlay_enabled
  );

  const lomavisCreator = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  const overlay_enabled =
    lomavisCreator.general_post_type === POST_TYPE_TEMPLATE || postIsOverlay;
  const hasTemplatePreview = shouldShowOverlayTemplatePreview({
    templateMode,
    templatePreviewUrl,
    postData
  });
  // Only media the overlay is still owed to holds the preview back. Anything
  // uploaded after it is unedited on purpose — it takes the ordinary path, and
  // reading its "not yet edited" flag as "still pending" left the whole preview
  // inert: no second slide, no editor, until that image was deleted again.
  const hasPendingTemplateMedia =
    templateMode &&
    Object.values(postData).some(
      (platform: any) =>
        getOverlayTemplateEligibleMedia(platform?.media).length > 0
    );
  const shouldLockTemplatePreviewActions = hasPendingTemplateMedia;
  const shouldShowPreview = showPreview(lomavisCreator) || hasTemplatePreview;
  // useClick away to close the dropdown
  useClickAway(previewModeRef, () => {
    setPreviewModeOpen(false);
  });
  const previewRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const simpleBarRef = useRef(null);
  const handleScrollToPreview = (platform: string) => {
    const scrollElement = simpleBarRef?.current?.getScrollElement();
    let targetElement = previewRefs.current[platform];
    if (!targetElement) return;
    scrollElement.scrollTop = 0;
    targetElement = previewRefs.current[platform];
    const offset = targetElement.getBoundingClientRect().top;
    if (targetElement) {
      scrollElement.scrollTop =
        offset + previewNavbarRef?.current.getBoundingClientRect().height - 200;
    }
  };
  const [lastHandledPostData, setLastHandledPostData] = useState(null);
  const [lastHandledOverlayData, setlastHandledOverlayData] = useState(null);
  // Cache object URLs by Blob identity so the same Blob always resolves to the
  // same URL. Without this, every keystroke re-runs getMediaFiles and produces
  // a fresh blob: URL, which changes the <video key={videoUrl}> and forces a
  // full reload of the preview video.
  const blobUrlCache = useRef<WeakMap<Blob, string>>(new WeakMap());
  const getBlobUrl = (blob: Blob): string => {
    let url = blobUrlCache.current.get(blob);
    if (!url) {
      url = URL.createObjectURL(blob);
      blobUrlCache.current.set(blob, url);
    }
    return url;
  };
  async function fetchData(postData) {
    const mediaFilesMapTmp: MediaFilesMap = {};
    for (const platform in postData) {
      if (postData[platform]?.media?.length > 0) {
        const mediaFiles = await getMediaFiles(platform);
        mediaFilesMapTmp[platform] = mediaFiles;
      }
    }
    setMediaFilesMap(mediaFilesMapTmp);
  }

  useEffect(() => {
    if (
      lastHandledPostData === postData &&
      lastHandledOverlayData === overlayData
    )
      return;
    fetchData(postData);
    setlastHandledOverlayData(overlayData);
    setLastHandledPostData(postData);
  }, [
    postData,
    overlayData,
    overlay_enabled
  ]);

  const getMediaFiles = async (platform) => {
    const mediaData = Array.isArray(postData[platform]?.media)
      ? postData[platform].media
          .map((item, index) => ({
            uuid: item?.uuid,
            type: item.media_type?.toLowerCase(),
            thumbnail_url: item?.thumbnail_image,
            custom_thumbnail_url:
              postData[platform]?.video_thumbnail?.preview_image,
            file_type: item.media_type?.toLowerCase(),
            video_url: item.original_asset,
            file: item.file instanceof Blob ? getBlobUrl(item.file) : null,
            image_url: item.preview_image,
            width: item.width,
            height: item.height,
            uploaded: item.uploaded,
            assetBlob: item?.assetBlob,
            original_asset: item?.original_asset,
            source_media_uuid: item?.source_media_uuid,
            apply_brand_overlay: false,
            upload_to_platform_status:
              postData[platform]?.upload_to_platform_status,
            index
          }))
          .filter((item) => item.uploaded || item.uploaded === undefined)
      : [];
    const mediaFilesCropped = [];
    for (const [index, mediaFile] of mediaData.entries()) {
      if (
        (postData[platform]?.cropList || [])
          .map((crop) => crop.uuid)
          .includes(mediaFile.uuid) ||
        true
      ) {
        const crop = (postData[platform]?.cropList || []).find(
          (crop) => crop.uuid === mediaFile.uuid || crop.uuid === 'whatever'
        );
        const getOverlayDataByPosition = (positionToFind) => {
          return overlayData.find((item) => item.position === positionToFind);
        };

        const getOverlayDataByUuid = (uuid) => {
          return overlayData.find((item) => item?.image === uuid);
        };

        // run the new function.

        const checkIfImageIsNotAnEdit = () => {
          const overlayObj = getOverlayDataByUuid(mediaFile?.uuid);

          if (overlayObj?.image === mediaFile.uuid) {
            return true;
          } else {
            return false;
          }
        };

        const replaceColors = () => {
          if (
            locationUuid &&
            !(
              mediaFile?.upload_to_platform_status >= POST_PUBLISHED_ON_PLATFORM
            )
          ) {
            return true;
          } else {
            return false;
          }
        };

        const overlayDataAsText = JSON.stringify(overlayData);

        const md5 = new Md5();
        const overlayDataMd5 = md5
          .appendStr(overlayDataAsText)
          .appendStr(mediaFile.image_url)
          .end();

        let cacheKey = `${overlayDataMd5}`;

        if (!crop) {
          if (
            overlay_enabled &&
            overlayData?.length > 0 &&
            checkIfImageIsNotAnEdit()
          ) {
            if (cropsCache[cacheKey]) {
              mediaFilesCropped.push(cropsCache[cacheKey]);
              continue;
            }

            // @ts-ignore
            if (window.lomavisCache) {
              // @ts-ignore
              const lomavisWindowCache = window.lomavisCache || {};
              if (lomavisWindowCache[cacheKey]) {
                mediaFilesCropped.push(lomavisWindowCache[cacheKey]);
                continue;
              }
            }

            const { blobUrl, blob } = await createCanvasWithOverlay(
              mediaFile.original_asset,
              getOverlayDataByUuid(mediaFile.uuid),
              brandData,
              replaceColors()
            );
            dispatch(
              updateTemplateBlob({
                uuid: mediaFile.uuid,
                blob: blob
              })
            );

            const mediaFileCopy = { ...mediaFile };
            mediaFileCopy.image_url = blobUrl;
            (mediaFileCopy.apply_brand_overlay = true),
              mediaFilesCropped.push(mediaFileCopy);

            // @ts-ignore
            const lomavisWindowCache = window.lomavisCache || {};
            lomavisWindowCache[cacheKey] = mediaFileCopy;
            // @ts-ignore
            window.lomavisCache = lomavisWindowCache;

            dispatch(
              saveCroppedImageCache({ key: cacheKey, obj: mediaFileCopy })
            );
          } else {
            mediaFilesCropped.push(mediaFile);
          }

          continue;
        }

        cacheKey = `${mediaFile.image_url}-${crop.width}-${crop.height}-${crop.x}-${crop.y}-${overlayDataMd5}`;

        if (cropsCache[cacheKey]) {
          mediaFilesCropped.push(cropsCache[cacheKey]);
          continue;
        }
        try {
          // recalculate the crop based on the image size on data and the actual image size
          const image = new Image();
          image.src = mediaFile.image_url;
          await new Promise((resolve) => {
            image.onload = resolve;
          });
          const imageWidth = image.width;
          const ScaleFactor = imageWidth / mediaFile.width;
          const newCrop = {
            x: crop.x * ScaleFactor,
            y: crop.y * ScaleFactor,
            width: crop.width * ScaleFactor,
            height: crop.height * ScaleFactor
          };
          const croppedImageUrl = await cropImage(mediaFile.image_url, newCrop);

          const croppedImageUrl_Overlay = await cropImage(
            mediaFile.original_asset,
            newCrop
          );

          const mediaFileCopy = { ...mediaFile };

          if (
            overlay_enabled &&
            overlayData?.length > 0 &&
            checkIfImageIsNotAnEdit()
          ) {
            // @ts-ignore
            if (window.lomavisCache) {
              // @ts-ignore
              const lomavisWindowCache = window.lomavisCache || {};
              if (lomavisWindowCache[cacheKey]) {
                mediaFilesCropped.push(lomavisWindowCache[cacheKey]);
                continue;
              }
            }

            const { blobUrl, blob } = await createCanvasWithOverlay(
              croppedImageUrl_Overlay,
              getOverlayDataByUuid(mediaFile.uuid),
              brandData,
              replaceColors()
            );
            dispatch(
              updateTemplateBlob({
                uuid: mediaFile.uuid,
                blob: blob
              })
            );
            mediaFileCopy.apply_brand_overlay = true;
            mediaFileCopy.image_url = blobUrl;
          } else {
            mediaFileCopy.image_url = croppedImageUrl;
          }

          mediaFileCopy.width = crop.width;
          mediaFileCopy.height = crop.height;
          dispatch(
            saveCroppedImageCache({ key: cacheKey, obj: mediaFileCopy })
          );
          mediaFilesCropped.push(mediaFileCopy);
        } catch (e) {
          console.error(e);
          mediaFilesCropped.push(mediaFile);
        }
      }
    }
    return mediaFilesCropped;
  };

  const platforms = [
    {
      name: FACEBOOK,
      component: {
        [POST_TYPE_POST_KEY]: FacebookPostPreview,
        [POST_TYPE_STORY_KEY]: InstagramFacebookStory,
        [POST_TYPE_REEL_KEY]: InstagramFacebookStory
      }
    },
    {
      name: INSTAGRAM,
      component: {
        [POST_TYPE_POST_KEY]: InstagramPostPreview,
        [POST_TYPE_STORY_KEY]: InstagramFacebookStory,
        [POST_TYPE_REEL_KEY]: InstagramPostPreview,
        [POST_TYPE_TRIAL_REEL_KEY]: InstagramPostPreview
      }
    },
    {
      name: LINKEDIN_PERSONAL_ACCOUNT,
      component: { [POST_TYPE_POST_KEY]: LinkedinPostPreview }
    },
    {
      name: LINKEDIN_BUSINESS_PAGE,
      component: { [POST_TYPE_POST_KEY]: LinkedinPostPreview }
    },
    {
      name: TIKTOK_PERSONAL_ACCOUNT,
      component: { [POST_TYPE_POST_KEY]: TiktokPostPreview }
    },
    {
      name: TIKTOK_BUSINESS_PAGE,
      component: { [POST_TYPE_POST_KEY]: TiktokPostPreview }
    },
    { name: YOUTUBE, component: { [POST_TYPE_VIDEO_KEY]: YoutubePostPreview } },
    { name: GOOGLE, component: { [POST_TYPE_POST_KEY]: GooglePostPreview } },
    { name: TWITTER, component: { [POST_TYPE_POST_KEY]: TwitterPostPreview } },

    { name: GENERIC, component: { [POST_TYPE_POST_KEY]: GenericPostPreview } },
    { name: WHATSAPP, component: { [POST_TYPE_MESSAGE]: WhatsAppPostPreview } },
    { name: PINTEREST, component: { [POST_TYPE_POST_KEY]: GenericPostPreview } }
  ].sort((a, b) => {
    return PLATFORM_LIST.indexOf(a.name) - PLATFORM_LIST.indexOf(b.name);
  });
  const previewNavbarRef = useRef(null);
  const templatePreviewPlatform =
    platforms.find((platform) => platformEnabled[platform.name])?.name ??
    GENERIC;
  const templatePreviewProfile =
    hasTemplatePreview && lomavisCreatorConfig?.connected_platforms
      ? getProfileDataForPlatform(templatePreviewPlatform, lomavisCreatorConfig)
      : undefined;
  const templatePreviewMedia = [
    {
      uuid: 'overlay-template-preview',
      type: 'image',
      file_type: 'image',
      image_url: templatePreviewUrl,
      original_asset: templatePreviewUrl,
      uploaded: true,
      index: 0
    }
  ];

  const getNavBarHeight = () => {
    if (previewNavbarRef?.current) {
      const navHeight = previewNavbarRef.current.getBoundingClientRect().height;
      return navHeight;
    }
    return 0;
  };

  return (
    <div className="w-100  p-5 pe-0 h-100 d-flex flex-column rightSideElm">
      {/* <link
        href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
        rel="stylesheet"
      ></link> */}
      <link href={DEFAULT_EDITOR_FONT_URL} rel="stylesheet"></link>
      {shouldShowPreview ? (
        <div
          className="d-flex justify-content-between w-100 pe-3 pb-3 align-items-center"
          ref={previewNavbarRef}
        >
          {!hasTemplatePreview && (
            <div className=" w-100 d-flex flex-row flex-wrap justify-content-start">
              {getPlatformsWithPreview(lomavisCreator).length < 7 ? (
                Object.keys(postData)
                  .filter((platform) => platformEnabled[platform])
                  .map((platform) => {
                    if (platform === ALL) return null;
                    if (
                      postData[platform]?.media?.length > 0 ||
                      postData[platform]?.text_length_striped > 0
                    ) {
                      return (
                        <div key={platform}>
                          <i
                            key={platform}
                            style={{ fontSize: 20 }}
                            className={`${ALL_PLATFORM_ICONS[platform]} text-white me-2 cursor-pointer`}
                            onClick={() => {
                              handleScrollToPreview(platform);
                            }}
                          ></i>
                        </div>
                      );
                    }
                  })
              ) : (
                <div className="dropdown">
                  <button
                    className="btn btn-secondary dropdown-toggle btn-sm"
                    type="button"
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {t('lomavisCreator.previewPost.jump_to')}
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="dropdownMenuButton"
                  >
                    {getPlatformsWithPreview(lomavisCreator).map((platform) => {
                      return (
                        <li key={platform}>
                          <div
                            className="dropdown-item"
                            onClick={() => {
                              handleScrollToPreview(platform);
                            }}
                          >
                            <i
                              style={{
                                color: PLATFORM_COLORS[platform],
                                fontSize: 20
                              }}
                              className={`${ALL_PLATFORM_ICONS[platform]} w-30px me-2 cursor-pointer text-center`}
                            ></i>
                            {t(getPlatformTranslationKey(platform))}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="">
            <Dropdown
              ref={previewModeRef}
              onClick={() => {
                setPreviewModeOpen(!previewModeOpen);
              }}
              show={previewModeOpen}
            >
              <Dropdown.Toggle
                variant="secondary"
                id="dropdown-basic"
                size="sm"
              >
                <span>
                  <i className="fa-light fa-magnifying-glass-plus me-1"></i>

                  {t(`lomavisCreator.previewPost.${previewMode}`)}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {[PREVIEW_TYPE_STANDARD, PREVIEW_TYPE_COMPACT].map((type) => (
                  <Dropdown.Item
                    key={type}
                    onClick={() => {
                      dispatch(switchPreviewMode({ mode: type }));
                    }}
                  >
                    {t(`lomavisCreator.previewPost.${type}`)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      ) : (
        <></>
      )}
      {shouldShowPreview && (
        <SimpleBar
          ref={simpleBarRef}
          forceVisible="y"
          autoHide={false}
          style={{ maxHeight: `calc(100% - ${getNavBarHeight()}px)` }}
        >
          <div className="mt-5 w-100 h-100 d-flex flex-column align-items-center">
            {hasTemplatePreview ? (
              <div
                className="mb-5 pb-5"
                style={{ width: PREVIEW_CARD_WIDTH }}
              >
                {/* Nothing on this screen says what to do next: the post is a
                    preview of an overlay with no image under it yet, and the
                    upload it needs is on the other side of the creator. */}
                <div
                  className="d-flex align-items-start gap-3 rounded bg-white bg-opacity-10 p-3 mb-3 text-white"
                  role="status"
                >
                  <i
                    className="fa-duotone fa-image fs-3 mt-1"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="mb-1 fw-semibold">
                      {t(
                        'lomavisCreator.overlayEditor.addImageGuide.title',
                        'Add an image to continue'
                      )}
                    </p>
                    <p className="mb-0 small text-white opacity-75">
                      {t(
                        'lomavisCreator.overlayEditor.addImageGuide.description',
                        'This is a preview of your overlay. It is placed on the first image you add.'
                      )}
                    </p>
                  </div>
                </div>
                <GenericPostPreview
                  mediaFiles={templatePreviewMedia}
                  postText={postData[ALL]?.text_as_html || ''}
                  profileName={
                    templatePreviewProfile?.name ??
                    t(getPlatformTranslationKey(GENERIC))
                  }
                  platformIcon={templatePreviewProfile?.platform}
                  platform={templatePreviewPlatform}
                  readOnly
                />
              </div>
            ) : (
              mediaFilesMap &&
              platforms
                .filter((data) => platformEnabled[data.name])
                .map((data) => {
                  const postType: any = postTypeSelected[data.name];
                  const { name, component } = data;
                  const mediaFiles = mediaFilesMap[name] || [];
                  const postText = postData[name]?.text_as_html || '';
                  if (
                    mediaFiles.length > 0 ||
                    postData[name]?.text_length_striped > 0
                  ) {
                    const Component = component[postType] || GenericPostPreview;
                    if (!postHasPreview) setPostHasPreview(true);
                    return (
                      <div
                        key={name}
                        className={`mb-5 pb-5 ${
                          shouldLockTemplatePreviewActions
                            ? 'overlay-template-preview-card'
                            : ''
                        }`}
                        id={`#${name}-preview`}
                        style={{
                          width: PREVIEW_CARD_WIDTH,
                          pointerEvents:
                            shouldLockTemplatePreviewActions &&
                            OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR.disablePlatformPreviewActions
                              ? 'none'
                              : undefined
                        }}
                        ref={(el) => (previewRefs.current[name] = el)}
                      >
                        <PostPreviewTitle platform={name} />
                        <Component
                          previewUrl={postData[name]?.urlPreview}
                          platform={name}
                          key={name}
                          cropList={[]}
                          mediaFiles={mediaFiles}
                          postText={postText}
                          profileName={
                            getProfileDataForPlatform(
                              name,
                              lomavisCreatorConfig
                            )?.name
                          }
                          platformIcon={
                            getProfileDataForPlatform(
                              name,
                              lomavisCreatorConfig
                            )?.platform
                          }
                          previewAssets={(currentIndex = null) => {
                            platformEditorSwitch(name, mediaFiles);
                          }}
                          viewAssets={(currentIndex = null) => {
                            handleMediaView(name, currentIndex);
                          }}
                        />
                      </div>
                    );
                  }
                })
            )}
          </div>
        </SimpleBar>
      )}
      {mediaFilesMap &&
        !Object.values(mediaFilesMap).some((arr) => arr?.length > 0) &&
        Object.values(postData).some((arr) => arr?.media.length > 0) &&
        shouldShowPreview && (
          <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100 gap-3">
            <div
              className="d-flex justify-content-center align-item-center w-100"
              style={{ paddingTop: '25%' }}
            >
              <div
                className="spinner-border text-muted"
                role="status"
                style={{
                  borderWidth: '5px',
                  borderRadius: '50%',
                  height: '30px',
                  width: '30px'
                }}
              ></div>
            </div>
            <span className="text-light mt-2">
              {t('lomavisCreator.previewPost.brandingLoad')}
            </span>
          </div>
        )}

      {!shouldShowPreview && (
        <div className="d-flex justify-content-center w-100 h-100 align-items-center">
          <span className="text-light">
            {t('lomavisCreator.previewPost.noPostPreviewYet')}
          </span>
        </div>
      )}
    </div>
  );
};

export default MediaUploadPreview;
